import React, { useState, useEffect, useMemo } from "react";
import {
  IndeterminateProgressCircle,
  DialogFooter,
  Column,
  Grid,
  Row,
  Toast,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
  TOAST_POSITIONS,
  SimpleAccordion,
  Button,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  INPUT_SIZES,
  EmailInput,
  Card,
} from "cdk-radial";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import ChangeLog from "./ChangeLog";
import RoleList from "./RoleList";
import styled from "styled-components";

const DupEmailInput = styled(EmailInput)`
  width: 60%;
`;

const PermissionsCard = styled(Card)`
  margin: 12px;
`;

const TOAST_TYPES = {
  PULL_SUCCESS: {
    content: "Successfully pulled roles from user",
    variant: TOAST_VARIANTS.POSITIVE,
  },
  PULL_ERROR: {
    content: "Failed to pull roles, reload page and try again",
    variant: TOAST_VARIANTS.NEGATIVE,
  },
  SAVE_SUCCESS: {
    content: "User roles have been updated",
    variant: TOAST_VARIANTS.POSITIVE,
  },
  ASSIGNMENT_ERROR: {
    content: "Failed to assign roles, reload page and try again",
    variant: TOAST_VARIANTS.NEGATIVE,
  },
  REVOKE_ERROR: {
    content: "Failed to remove roles, reload page and try again",
    variant: TOAST_VARIANTS.NEGATIVE,
  },
  POST_ERROR: {
    content: "Failed to update user's roles, reload page and try again",
    variant: TOAST_VARIANTS.NEGATIVE,
  },
};

// Helper function to sort roles by name
function sortByRoleName(roleList) {
  return roleList.sort((a, b) => a.name.localeCompare(b.name));
}

// Helper function to get 'set differences' for role lists
function getDifferencesOfRoleLists(list1, list2) {
  const list1Ids = list1.map((role) => role.id);
  const list2Ids = list2.map((role) => role.id);

  const aDiffList = list1.filter((role) => !list2Ids.includes(role.id));
  const bDiffList = list2.filter((role) => !list1Ids.includes(role.id));

  return aDiffList.length > 0 || bDiffList.length > 0
    ? [aDiffList, bDiffList]
    : [];
}

const PermissionsEditor = (props) => {
  const { show } = props;
  const [displayedRolesList, setDisplayedRolesList] = useState([]);
  const [selectedRolesList, setSelectedRolesList] = useState([]);
  const [openPanels, setOpenPanels] = useState({});
  const [dupEmail, setDupEmail] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [reasonHasError, setReasonHasError] = useState(false);
  const [activeToast, setActiveToast] = useState(null);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [dupUserProfile, setDupUserProfile] = useState({});

  const emailKey = props.userEmail?.toLowerCase();
  const userProfile = props.userProfile.profileDetails?.[emailKey] || {};
  const canAssignRoles =
    props.auth.entitlements?.["*"]?.perm?.includes("assign");

  const allRolesList = useMemo(() =>
    sortByRoleName(props.userRoles.allRolesList || [])
  );

  const userId = useMemo(
    () => userProfile.userDetails?.id,
    [userProfile.userDetails]
  );
  const userFetch = useMemo(
    () => props.userRoles.userFetches?.[userId],
    [props.userRoles.userFetches]
  );
  const userRolesList = useMemo(() => {
    if (!Array.isArray(userFetch?.userRolesList)) return [];

    const filtered = userFetch.userRolesList.filter(
      (role) => role.type === "platform"
    );

    return sortByRoleName(filtered);
  }, [userFetch?.userRolesList]);

  const dupUserId = useMemo(
    () => dupUserProfile?.userDetails?.id,
    [dupUserProfile?.userDetails]
  );

  const dupUserFetch = useMemo(
    () => props.userRoles.userFetches?.[dupUserId],
    [props.userRoles.userFetches]
  );

  const selectedAndAllDiff = useMemo(() => {
    return getDifferencesOfRoleLists(selectedRolesList, userRolesList);
  }, [selectedRolesList, userRolesList]);

  const globalDisable =
    props.userProfile.profileDetails[dupEmail]?.isFetching ||
    dupUserFetch?.isFetchingUserRoles ||
    props.userRoles.makingChanges;

  useEffect(() => {
    if (props.userRoles.isFetchedAllRoles && userFetch?.userRolesList) {
      setSelectedRolesList(
        allRolesList.filter((role) =>
          userRolesList.some((userRole) => userRole.id === role.id)
        )
      );
    }
  }, [props.userRoles.isFetchedAllRoles, userFetch?.userRolesList]);

  useEffect(() => {
    const dupProfile = props.userProfile.profileDetails[dupEmail.toLowerCase()];

    if (!dupProfile?.isFetching && dupProfile?.userDetails) {
      handleFetchRoles(dupProfile);
    } else if (props.userProfile.error) {
      setIsInvalidEmail(true);
    }
  }, [props.userProfile.profileDetails]);

  useEffect(() => {
    const userId = dupUserProfile?.userDetails?.id;
    const userFetch = props.userRoles.userFetches[userId];

    if (userFetch?.isFetchedUserRoles) {
      const dupUserRoles = [...userFetch.userRolesList];
      const [pullDiff = [], _] = getDifferencesOfRoleLists(
        dupUserRoles,
        selectedRolesList
      );
      setSelectedRolesList((prev) => sortByRoleName([...prev, ...pullDiff]));
      setActiveToast(TOAST_TYPES.PULL_SUCCESS);
    }
  }, [props.userRoles.userFetches[dupUserProfile?.userDetails?.id]]);

  const handleFetchRoles = async (dupProfile) => {
    const postResponse = await props.fetchUserRoles({
      uid: dupProfile.userDetails.id,
      accessToken: props.auth.accessToken,
    });
    if (props.userRoles.hasError || postResponse.apierror) {
      setActiveToast(TOAST_TYPES.PULL_ERROR);
      props.resetRoleErrors();
    } else {
      setDupUserProfile(dupProfile);
    }
  };

  const handleCheckChange = (role) => {
    const roleId = role.id;
    setSelectedRolesList((prev) => {
      const prevSelectedRoleIds = prev.map((prevRole) => prevRole.id);

      const nextSelectedRolesList = prevSelectedRoleIds.includes(roleId)
        ? prev.filter((prevRole) => prevRole.id !== role.id)
        : [...prev, role];

      return nextSelectedRolesList;
    });

    setReasonHasError(false);
    setReasonText("");
  };

  const handleSave = async () => {
    if (reasonText.trim() !== "") {
      setReasonHasError(false);
      setReasonText("");
      const [addedRolesList, revokedRolesList] = getDifferencesOfRoleLists(
        selectedRolesList,
        userRolesList
      );

      const addedRoleIds = addedRolesList.map((r) => r.id);
      const revokedRoleIds = revokedRolesList.map((r) => r.id);

      let hasAssignError = false;
      let hasRevokeError = false;

      if (addedRoleIds.length > 0) {
        const postResponse = await props.assignRolesToUser({
          uid: userProfile.userDetails.id,
          orgId: "*",
          orgName: "",
          roleIds: addedRoleIds,
          accessToken: props.auth.accessToken,
          reason: reasonText,
        });
        if (postResponse.apierror) {
          hasAssignError = true;
        }
      }

      if (revokedRoleIds.length > 0) {
        const postResponse = await props.revokeRolesFromUser({
          uid: userProfile.userDetails.id,
          orgId: "*",
          orgName: "",
          roleIds: revokedRoleIds,
          accessToken: props.auth.accessToken,
          reason: reasonText,
        });
        if (postResponse.apierror) {
          hasRevokeError = true;
        }
      }
      if (hasAssignError || hasRevokeError) {
        setActiveToast(
          hasAssignError
            ? hasRevokeError
              ? TOAST_TYPES.POST_ERROR
              : TOAST_TYPES.ASSIGNMENT_ERROR
            : TOAST_TYPES.REVOKE_ERROR
        );
        props.resetRoleErrors();
      } else {
        props.roleChangesMade({ uid: userProfile.userDetails.id });
        setActiveToast(TOAST_TYPES.SAVE_SUCCESS);
      }
    } else {
      setReasonHasError(true);
    }
  };

  const handleRevert = () => {
    setSelectedRolesList(
      allRolesList.filter((role) =>
        userRolesList.some((userRole) => userRole.id === role.id)
      )
    );
    setReasonText("");
    setReasonHasError(false);
  };

  const handleReasonChange = (event) => {
    setReasonText(event.target.value);
  };

  const handleDupSearchChange = (event) => {
    setDupEmail(event.target.value);
    setIsInvalidEmail(false);
  };

  const handleDuplicateClick = () => {
    if (dupEmail.toLowerCase() !== emailKey) {
      props.getUserDetails(dupEmail.toLowerCase(), props.auth.accessToken);
    } else {
      setIsInvalidEmail(true);
    }
  };

  const handlePanelToggle = (id) => {
    const isOpen = openPanels[id];
    setOpenPanels({
      ...openPanels,
      [id]: !isOpen,
    });
  };

  if (reasonHasError && reasonText.trim() !== "") {
    setReasonHasError(false);
  }

  const userRolesFetched =
    userId && props.userRoles.userFetches?.[userId]?.isFetchedUserRoles;

  if (!props.userRoles.isFetchedAllRoles || !userRolesFetched) {
    return (
      <>
        {show && (
          <div className="loading-container">
            <h4>Loading Roles</h4>
            <IndeterminateProgressCircle />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {show && (
        <>
          {activeToast && (
            <Toast
              content={activeToast.content}
              id="toast"
              onVisibilityDurationEnd={() => {
                setActiveToast(null);
              }}
              visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
              variant={activeToast.variant}
              position={TOAST_POSITIONS.FIXED}
            />
          )}
          {canAssignRoles && (
            <SimpleAccordion
              data-testid="duplicate-accordion"
              items={[
                {
                  content: (
                    <div
                      style={{
                        margin: "16px",
                        display: "flex",
                        justifyContent: "space-around",
                      }}
                    >
                      <DupEmailInput
                        enableCustomValidation={true}
                        dataTestId="duplicate-email-input"
                        errorMessage="Invalid Email"
                        hasError={isInvalidEmail}
                        helperText="Enter email of user to pull from"
                        iconPosition="trailing"
                        id="email-input"
                        label="Email"
                        maxInlineLabelWidth="200px"
                        name="duplicate-email-input"
                        onChange={handleDupSearchChange}
                        placeholder="abc@example.com"
                        size={INPUT_SIZES.STANDARD}
                        value={dupEmail}
                        isDisabled={globalDisable}
                      />
                      <Button
                        style={{ marginTop: "5px" }}
                        dataTestId="duplicate-pull-button"
                        iconColor="primary"
                        iconSize={24}
                        onClick={handleDuplicateClick}
                        size={BUTTON_SIZES.STANDARD}
                        text="Pull Roles"
                        variant={BUTTON_VARIANTS.PRIMARY}
                        isLoading={
                          props.userProfile.profileDetails[dupEmail]
                            ?.isFetching || dupUserFetch?.isFetchingUserRoles
                        }
                        isDisabled={isInvalidEmail || globalDisable}
                      />
                    </div>
                  ),
                  id: "duplicate-accordion",
                  title: "Duplicate Roles from User",
                },
              ]}
              onPanelToggle={handlePanelToggle}
              openPanels={openPanels}
              showDivider
            />
          )}
          <Grid>
            <Row>
              <Column
                xl={canAssignRoles ? 9 : 12}
                l={canAssignRoles ? 9 : 12}
                m={12}
                s={12}
                xs={12}
              >
                <PermissionsCard>
                  <RoleList
                    selectedRolesList={selectedRolesList}
                    allRolesList={allRolesList}
                    setDisplayedRolesList={setDisplayedRolesList}
                    sortByRoleName={sortByRoleName}
                    globalDisable={globalDisable}
                    displayedRolesList={displayedRolesList}
                    handleCheckChange={handleCheckChange}
                    canAssignRoles={canAssignRoles}
                  />
                </PermissionsCard>
              </Column>
              <Column xl={3} l={3} m={12} s={12} xs={12}>
                {!props.userRoles.makingChanges &&
                  selectedAndAllDiff.length > 0 && (
                    <PermissionsCard>
                      <ChangeLog
                        diffLists={selectedAndAllDiff}
                        reasonHasError={reasonHasError}
                        reasonText={reasonText}
                        handleReasonChange={handleReasonChange}
                      />
                    </PermissionsCard>
                  )}
              </Column>
            </Row>
          </Grid>
          {canAssignRoles && (
            <DialogFooter
              className="permissions-footer"
              buttonsProps={[
                {
                  id: "save-button",
                  dataTestId: "save-button",
                  onClick: handleSave,
                  text: "Save",
                  isDisabled:
                    selectedAndAllDiff.length === 0 ||
                    globalDisable ||
                    reasonHasError,
                },
                {
                  id: "revert-button",
                  dataTestId: "revert-button",
                  onClick: handleRevert,
                  text: "Revert",
                  variant: "text",
                  isDisabled: selectedAndAllDiff.length === 0 || globalDisable,
                },
              ]}
            />
          )}
        </>
      )}
    </>
  );
};

export default withAuth(PermissionsEditor);
